import { useNavigateBack } from '@iheartjane/menu';
import {
  Box,
  CheckboxField,
  Flex,
  Link,
  Loading,
  Typography,
} from '@iheartjane/reefer';

import { useKioskSpacing } from '../../../../hooks';
import { useCheckout } from '../../../../providers';
import type { KioskAgreementLink } from '../../../../util';
import { isConsented, toAgreementLinks } from '../../../../util';
import { CheckoutFlowButton } from '../../CustomerInfoFields/CustomerInfoFields';
import { Modal } from '../../Modal';
import { ButtonWrapper } from '../checkout.styles';
import { AgreementsModalHeader } from './AgreementsModalHeader';

import styles from './CheckoutAgreementsScreen.module.css';

/**
 * First step of kiosk checkout: a consent checkbox per store agreement. The
 * shopper can't reach the customer info form until every required agreement is
 * checked. Tapping an agreement link swaps the modal to its QR code page.
 */
export const CheckoutAgreementsScreen = () => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const {
    acceptAgreements,
    agreementsError,
    agreementsLoading,
    checkoutAgreements,
    consents,
    requiredConsentsValid,
    setAgreementLinkInView,
    setConsents,
    store,
  } = useCheckout();

  const goBack = useNavigateBack(`stores/${store.id}/menu`);

  // Fail closed: a slow or failed load must not let a shopper past consent.
  const canContinue =
    !agreementsLoading && !agreementsError && requiredConsentsValid;

  const handleChange = (id: number, checked: boolean) =>
    setConsents((prev) => ({ ...prev, [String(id)]: checked }));

  const viewLink = (link: KioskAgreementLink) => setAgreementLinkInView(link);

  return (
    <>
      <AgreementsModalHeader />

      <Modal.Content overflowY="auto" padding={false}>
        <Flex
          flexDirection="column"
          gap={getKioskSpacing(24, 16)}
          px={getKioskSpacing(32, 40)}
          py={getKioskSpacing(24, 16)}
        >
          {agreementsLoading && (
            <Box position="relative" py={24}>
              <Loading color="black" size="lg" />
            </Box>
          )}

          {agreementsError && (
            <Typography color="error">
              We couldn&apos;t load the required agreements. Please try again.
            </Typography>
          )}

          {checkoutAgreements.map((item) => (
            <Flex flexDirection="column" gap={8} key={item.id}>
              <CheckboxField
                checked={isConsented(item, consents)}
                className={styles['agreement']}
                label={
                  <>
                    {item.required && (
                      <Typography as="span" color="error">
                        *
                      </Typography>
                    )}
                    {item.label}
                  </>
                }
                name={`kiosk-consent-${item.id}`}
                onChange={(checked) => handleChange(item.id, checked)}
              />
              {toAgreementLinks(item).map((link) => (
                <Flex className={styles['agreementLinks']} key={link.url}>
                  <Link
                    ariaLabel={`${link.label}, opens a QR code to scan`}
                    onClick={() => viewLink(link)}
                    variant="underline"
                  >
                    <Typography>{link.label}</Typography>
                  </Link>
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      </Modal.Content>

      <Flex
        flexDirection="column"
        pb={getKioskSpacing(32, 40)}
        px={getKioskSpacing(32, 40)}
        shrink={false}
      >
        <ButtonWrapper
          gap={getKioskSpacing(24, 16)}
          isIpad={true}
          marginTop={0}
        >
          <CheckoutFlowButton
            buttonLabel="Cancel"
            onClick={() => goBack()}
            size={isIpad ? 'small' : 'large'}
            variant="tertiary"
          />
          <CheckoutFlowButton
            ariaLabel="continue to enter your customer information"
            buttonLabel="Next"
            disabled={!canContinue}
            onClick={acceptAgreements}
            size={isIpad ? 'small' : 'large'}
          />
        </ButtonWrapper>
      </Flex>
    </>
  );
};
